// previewContext.tsx
import { type FC, createContext, useContext, useEffect, useMemo } from 'react'


import { usePreviewStore } from 'store/previewStore'

import Preview from '../features/Preview/Preview'
import PreviewHome from '../features/Preview/PreviewHome'


import './_modal.scss'

export const PreviewContext = createContext<any>(null)

/**
 * IMPORTANT:
 * - This component subscribes to preview STATE and renders the overlay.
 * - It is a child of PreviewProvider.
 * - Child can re-render without parent re-rendering.
 */
const PreviewModalHost: FC = () => {
  const previewOpen =
    usePreviewStore(s => s.previewOpen)

  const minimized =
    usePreviewStore(s => s.minimized)

  const previewLocation =
    usePreviewStore(s => s.previewLocation)

  const selectedPostType =
    usePreviewStore(
      s => s.state.selectedPostType
    )
    
  useEffect(() => {
    const html = document.documentElement
    const previousOverflow = html.style.overflow

    if (previewOpen) {
      html.style.overflow = 'hidden'
    }

    return () => {
      html.style.overflow = previousOverflow
    }
  }, [previewOpen])
  return (
    <div
      className={`previewModal previewModal--${
        previewOpen
          ? 'active'
          : 'inactive'
      } previewModal--${
        minimized
          ? 'minimized'
          : ''
      } ${
        selectedPostType === 'photo'
          ? 'photo'
          : ''
      } ${
        selectedPostType === 'video'
          ? 'video'
          : ''
      }`}
      style={
        previewOpen
          ? undefined
          : {
              visibility: 'hidden',
              pointerEvents: 'none'
            }
      }
    >
      {previewLocation === 'home' && (
        <PreviewHome />
      )}

      {previewLocation === 'profile' && (
        <Preview />
      )}

      {(previewLocation === 'purchased' ||
        previewLocation === 'watched' ||
        previewLocation === 'liked' ||
        previewLocation === 'playlist') && (
        <Preview />
      )}
    </div>
  )
}
// GO BACK TO BELOW IF KEEPING PREVIEW OPEN DOESNT WORK OUT
/* const PreviewModalHost: FC = () => {
  const previewOpen = usePreviewStore((s) => s.previewOpen)
  const minimized = usePreviewStore((s) => s.minimized)
  const previewLocation = usePreviewStore((s) => s.previewLocation)
  const selectedPostType = usePreviewStore((s) => s.state.selectedPostType)

  // hard unmount when closed
  if (!previewOpen) return null

  return (
    <div
      className={`previewModal previewModal--${previewOpen ? 'active' : 'inactive'} previewModal--${
        minimized ? 'minimized' : ''
      } ${selectedPostType === 'photo' ? 'photo' : ''} ${selectedPostType === 'video' ? 'video' : ''}`}
    >
      {previewLocation === 'home' && <PreviewHome />}
      {previewLocation === 'profile' && <Preview />}
      {(previewLocation === 'purchased' ||
        previewLocation === 'watched' ||
        previewLocation === 'liked' ||
        previewLocation === 'playlist') && <PreviewMedia />}
    </div>
  )
} */

export const PreviewProvider: FC<{ children?: any }> = ({ children }) => {
  /**
   * CRITICAL:
   * DO NOT subscribe to preview state here.
   * Only read ACTIONS (which are stable) so this provider does not re-render on open/close.
   */
  const addModal = usePreviewStore((s) => s.addModal)
  const clearModal = usePreviewStore((s) => s.clearModal)
  const updateSelectedPost = usePreviewStore((s) => s.updateSelectedPost)
  const setFilter = usePreviewStore((s) => s.setFilter)
  const setFilters = usePreviewStore((s) => s.setFilters)
  const setInitialData = usePreviewStore((s) => s.setInitialData)
  const handleMinimize = usePreviewStore((s) => s.handleMinimize)
  const setCommentsActive = usePreviewStore((s) => s.setCommentsActive)
  const setSelectedPostData = usePreviewStore((s) => s.setSelectedPostData)
  const resetPreviewState = usePreviewStore((s) => s.resetPreviewState)

  /**
   * Provide getters that read from store at call time.
   * This avoids subscribing the Provider, AND avoids stale values.
   */
  const value = useMemo(
    () => ({
      addModal,
      clearModal,
      updateSelectedPost,
      setFilter,
      setFilters,
      setInitialData,
      handleMinimize,
      setCommentsActive,
      setSelectedPostData,
      resetPreviewState,

      // state getters (NO subscription)
      get selectedPost() {
        return usePreviewStore.getState().state.selectedPost
      },
      get selectedSlide() {
        return usePreviewStore.getState().state.selectedSlide
      },
      get userId() {
        return usePreviewStore.getState().state.userId
      },
      get filter() {
        return usePreviewStore.getState().state.filter
      },
      get query() {
        return usePreviewStore.getState().state.query
      },
      get filterQuery() {
        return usePreviewStore.getState().filterQuery
      },
      get minimized() {
        return usePreviewStore.getState().minimized
      },
      get commentsActive() {
        return usePreviewStore.getState().commentsActive
      },
      get selectedPostData() {
        return usePreviewStore.getState().selectedPostData
      },
      get previewLocation() {
        return usePreviewStore.getState().previewLocation
      },

    }),
    [
      addModal,
      clearModal,
      updateSelectedPost,
      setFilter,
      setFilters,
      setInitialData,
      handleMinimize,
      setCommentsActive,
      setSelectedPostData,
      resetPreviewState,
    ]
  )

  return (
    <PreviewContext.Provider value={value}>
      {/* Renders and updates independently of children */}
      <PreviewModalHost />

      {/* Feed should stop re-rendering on open/close now */}
      {children}
    </PreviewContext.Provider>
  )
}

export const usePreviewContext = () => useContext(PreviewContext)
